
import * as React from 'react';
import { HotKeyNames } from './constants';
import { getElementByInnerText, getKeyByValue } from './utils';
import { RouteComponentProps } from 'react-router';
import { IActionBarProps } from './ActionBarContextProvider';
import { isAnyDialogOpened } from './helpers';

const LONG_PRESS_TIMEOUT = 500;

export const useElementSize = (ref?: React.MutableRefObject<any>, initialSize: { width?: number; height?: number } = { width: 0, height: 0 }) => {

    const [elementSize, setElementSize] = React.useState<{ width?: number; height?: number }>(initialSize);
    React.useEffect(
        () => {
            const handleResize = () => {
                const size = {
                    height: ref?.current?.offsetHeight ?? window.innerHeight,
                    width: ref?.current?.offsetWidth ?? window.innerWidth
                };
                setElementSize(size);
            };
            window.addEventListener('resize', handleResize);
            handleResize();

            return () => window.removeEventListener('resize', handleResize);
        },
        []);

    return elementSize;
};

type KeyHandlersMap = Partial<Record<HotKeyNames, (event: KeyboardEvent) => void>>;

// it returns handlers which you need to pass in HotKeys HOC
export const useMemoizedKeyHandlers = (keyHandles: KeyHandlersMap) => React.useMemo(
    () => (keyHandles),
    [keyHandles]
);

export interface ILocationState {
    search?: any;
    state?: any;
}

export const useModule = (
    history: RouteComponentProps['history'],
    location: RouteComponentProps['location'],
    defaultSelectedTab: string,
    tabLocations: any,
    modules: any[]
) => {
    const [selectedTab, setSelectedTab] = React.useState<string>(defaultSelectedTab);

    const handleModuleChange = React.useCallback(
        (newSelectedTab: string, locationObj?: ILocationState): void => {
            const pathname = (typeof tabLocations[newSelectedTab] === 'string') ? tabLocations[newSelectedTab] : tabLocations[newSelectedTab]?.pathname;
            const search = locationObj?.search ? `?${new URLSearchParams(locationObj?.search)}`
                : tabLocations[newSelectedTab]?.search ? `?${new URLSearchParams(tabLocations[newSelectedTab]?.search)}` : undefined;

            if (pathname) {
                history.push({
                    pathname,
                    search,
                    state: locationObj?.state ?? undefined
                });
            }

        },
        [tabLocations, history]
    );

    React.useEffect(
        () => {
            const pathname = location ? location.pathname : '';
            const tabByLocation = getKeyByValue(tabLocations, pathname);
            setSelectedTab(tabByLocation);
        },
        [location]);

    const navigateBack = React.useCallback(
        () => {
            const currentTabOnBackModule = modules.find((m) => m.id === selectedTab)?.onBack;
            if (currentTabOnBackModule) {
                handleModuleChange(currentTabOnBackModule);
            }
        },
        [modules, selectedTab, handleModuleChange]
    );

    return {
        selectedTab,
        setSelectedTab,
        handleModuleChange,
        navigateBack,
    };
};

/**
 * Returns onClick handler function which will call the passed callback when passed condition is satisfied.
 * Used for asynchronous click operation, where we need to wait for some promise to fullfil before calling click.
 * @param {Function} onClickCallback: onClickCallback callback function which you wanna call when condition met.
 * @param {Boolean} condition: boolean value based upon which onClickCallback will be called.
 * @returns {Function}
 */
export const useAsyncClickHandler = (onClickCallback: () => void, condition: boolean) => {
    const [clicked, setClicked] = React.useState<boolean>(false);

    React.useEffect(
        () => {
            if (condition && clicked && onClickCallback) {
                onClickCallback();
                setClicked(false);
            }
        },
        [condition, clicked, onClickCallback]
    );
    const onClick = React.useCallback(
        () => {
            setClicked(true);
        },
        []
    );

    const resetAsyncClick = () => {
        setClicked(false);
    };

    return { clicked, onClick, resetAsyncClick };
};

export const useActionBarHotKeys = (actionBar: IActionBarProps['actionBar']) => {
    const buttonHotkeysMeta = React.useMemo(() => actionBar?.leftIcons?.concat(actionBar?.centerIcons, actionBar?.rightIcons)?.map((b) => ({ label: b?.label, hotkey: b?.hotkey, multiHotKeys: b?.multiHotKeys })), [actionBar]);

    const validateAndCickButton = (innerText: string, actionToBind?: string) => (e) => {
        e?.preventDefault();
        if (isAnyDialogOpened()) {
            return;
        }

        const parkSaleButton = getElementByInnerText('span', innerText);

        const buttonElement = parkSaleButton?.parentElement?.parentElement as HTMLButtonElement;
        if (buttonElement && !buttonElement?.disabled) {

            if (actionToBind) {
                buttonElement.setAttribute('data-action', actionToBind);
            }

            buttonElement.click();
        }
    };

    const handlers = React.useMemo(
        () => buttonHotkeysMeta.reduce(
            (acc, { label, hotkey, multiHotKeys }) => {

                if (label && hotkey) {
                    return { ...acc, [hotkey?.toString()]: validateAndCickButton(label) };
                }

                if (multiHotKeys) {
                    Object.keys(multiHotKeys).forEach((hotKey) => {
                        acc[hotKey.toString()] = validateAndCickButton(label, multiHotKeys[hotKey].action);
                    });
                }

                return acc;
            },
            {}
        ),
        [buttonHotkeysMeta]
    );

    return { handlers };

};

export const useTriggerResize = () => {
    const triggerResize = React.useCallback(
        () => {
            const resizeEvent = new Event('resize');
            window.dispatchEvent(resizeEvent);
        },
        []
    );

    return triggerResize;
};

/**
 * Returns event handler functions for detecting long press actions (mouse down/touch start).
 * Executes the provided callback if the press lasts longer than the specified timeout (default: 500ms).
 * Can also trigger an optional callback when the press is released.
 *
 * @param {Function} onLongPress - Callback function to be called when a long press is detected.
 * @param {Function} [onPressEnd] - Optional callback function to be called when the press ends (on mouse up/touch end).
 * @param {Number} [timeout=500] - Optional timeout value (in milliseconds) to determine the long press duration.
 * @returns {Object} - Returns an object with event handlers: onMouseDown, onMouseUp, onTouchStart, onTouchEnd.
 */
export const useLongPress = (onLongPress: () => void, onPressEnd?: (event?: React.MouseEvent | React.TouchEvent) => void, disableLeftLongPress = false) => {
    const [longPress, setLongPress] = React.useState(false);
    const pressTimer = React.useRef<NodeJS.Timeout | null>(null);
    const startLongPress = (event: any = {}) => {
        const { button } = event;

        pressTimer.current = setTimeout(
            () => {
                // Skipping Logic for Long left Click
                if (disableLeftLongPress && button === 0) return;
                setLongPress(true);
                onLongPress();
            },
            LONG_PRESS_TIMEOUT
        );
    };
    const endLongPress = (event: any = {}) => {
        const { button } = event;
        if (pressTimer.current) {
            clearTimeout(pressTimer.current);
            pressTimer.current = null;
        }
        // Skipping Logic for Long left Click
        if (disableLeftLongPress && button === 0) return;
        if (longPress && onPressEnd) {
            onPressEnd(event);
        }
        setLongPress(false);
    };

    return {
        onMouseDown: startLongPress,
        onMouseUp: endLongPress,
        onTouchStart: startLongPress,
        onTouchEnd: endLongPress,
    };
};
/**
 * Returns onClick handler functions for detecting a single press action (mouse up/touch end),
 * and executes the provided callback only if a long press is not detected during the interaction.
 *
 * @param {Function} onSinglePress - Callback function to be called when a single press is detected (non-long press).
 * @returns {Object} - Returns an object with event handlers: onMouseUp, onTouchEnd.
 */
export const useSinglePress = (onSinglePress: () => void) => {
    const handleMouseUp = (event: MouseEvent) => {
        if (event.button === 0) {
            onSinglePress();
        }
    };

    return {
        onMouseUp: handleMouseUp,
    };
};
